import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { storeTimeout } from '@/shared/lib/storeTimeout';
import { KEY_RECOIL } from '@/shared/config';
import { selectActiveTab, selectBeatChanges, useReportStore } from '@/entities/report';
import { calculateStartStopStripMomentOfListBookmark } from '@/widgets/ecg-events/ui/handler';
import { calculateStartStopStripMomentOfListEventV2 } from '@/entities/event';
import { logError } from '@/shared/lib/apiHelpers';
import { checkAndProcessEcgData } from '@/entities/ecg';
import { checkAndProcessBeatData, generateUpdatedBeats } from '@/entities/beat';
import type { BookmarkPreviewStripProps } from './bookmarkPreviewStrip';

/**
 * Loads and holds the ECG waveform and beat markers behind a single bookmark preview strip. Reads the tab-2 active button and pending beat changes from the report store, fetches the five-second window around the bookmark centre, overlays the pending beat edits onto the fetched beats, and marks the strip deleted when its id sits in the pending delete list.
 *
 * @param props - Strip inputs: the bookmark, the ECG data map, its grid index and the custom offset.
 * @returns An object carrying isDeleted, ecgData, beatMarkers, hesBeatStatus, channels and the onClick handler.
 */
const useBookmarkPreviewStrip = ({
  onClickSelectEvent,
  channels: channelsProp,
  bookmark,
  ecgDataMap,
  customOffset,
  index,
}: Pick<
  BookmarkPreviewStripProps,
  'onClickSelectEvent' | 'channels' | 'bookmark' | 'ecgDataMap' | 'customOffset' | 'index'
>) => {
  const keyRecoil = KEY_RECOIL.RHYTHM_EVENT;
  const activeButton = useReportStore(selectActiveTab(keyRecoil));
  const beatChanges = useReportStore(selectBeatChanges(keyRecoil));
  const ecgBookmarkDeleteIds = useReportStore((s) => s.ecgBookmarkDeleteIds);
  const isDeleted = ecgBookmarkDeleteIds.includes(bookmark.id);
  const isRenderViewerEcg = useReportStore((s) => s.isRenderViewerEcg);

  const [ecgData, setEcgData] = useState([]);
  const [beatMarkers, setBeatMarkers] = useState([]);
  const [hesBeatStatus, setHesBeatStatus] = useState([]);

  const timeoutRef = useRef<any>();
  let isActiveQuery: boolean;

  /**
   * Warms the ECG cache for a bookmark's strip window so scrolling the grid does not refetch. Schedules a timer that deregisters itself from the tab's timeout store, computes the default strip start and stop from the bookmark's epoch, and requests that span's samples.
   *
   * @param waitTime - Delay in milliseconds before the cache request is issued.
   * @returns Nothing.
   */
  const downloadCacheECGDataV2 = (waitTime = 0) => {
    const timerId = setTimeout(() => {
      // storeTimeout (Store, Task 4) declares string keys; activeButton's atom type is wider - atom-type gap.
      storeTimeout.removeStore(activeButton as any, timerId);
      const { defaultStartStrip, defaultStopStrip } = calculateStartStopStripMomentOfListEventV2({
        startTimeEpoch: bookmark.startTimeEpoch,
      });
      checkAndProcessEcgData({
        ecgDataMap,
        dataLength:
          ((defaultStopStrip.valueOf() - defaultStartStrip.valueOf()) / 1000) *
          ecgDataMap.samplingFrequency,
        // checkAndProcessEcgData's momentObject type (common/handlerEcgData.ts, Task 7) declares
        // string | number, but the function itself does moment(momentObject.startMoment) - it accepts Moment too.
        momentObject: {
          startMoment: defaultStartStrip,
          stopMoment: defaultStopStrip,
        } as any,
      });
    }, waitTime);
    timeoutRef.current = timerId;
    storeTimeout.pushStore(activeButton as any, timerId);
  };

  /**
   * Fetches the five-second ECG window and beat data centred on the bookmark. Resolves the strip bounds from the bookmark centre and sampling frequency, awaits the waveform and beat queries, applies pending beat changes to the markers while the query is still active, then queues a background cache warm and logs anything other than a cancellation.
   *
   * @returns A promise resolving once the strip's waveform and beat markers are in state.
   */
  const downloadEventStrip = async () => {
    try {
      const { defaultStartStrip, defaultStopStrip } = calculateStartStopStripMomentOfListBookmark(
        bookmark.center,
        ecgDataMap.samplingFrequency,
      );
      const momentObject = {
        startMoment: defaultStartStrip,
        stopMoment: defaultStopStrip,
      } as any;
      let ecgData: any = await checkAndProcessEcgData({
        ecgDataMap,
        beat: bookmark,
        dataLength: ecgDataMap.samplingFrequency * 5, // 5 seconds
        config: { customOffset },
        momentObject,
      });
      // Check and process beat data
      let beatData: any = await checkAndProcessBeatData({
        ecgDataMap,
        beat: bookmark,
        momentObject,
      });
      // Update state with new data
      if (beatData && isActiveQuery) {
        setEcgData(ecgData);
        if (beatData) {
          const { updatedBeats, updatedHesBeatStatus } = generateUpdatedBeats(
            beatData?.beats,
            beatData?.hesBeatStatus,
            beatChanges,
            moment(defaultStartStrip).valueOf(),
            moment(defaultStopStrip).valueOf(),
          );
          setBeatMarkers(updatedBeats);
          setHesBeatStatus(updatedHesBeatStatus);
        }
        downloadCacheECGDataV2(index * 300);
      }
      // Nullify the data objects to help garbage collection
      ecgData = null;
      beatData = null;
    } catch (error) {
      if (!_.includes(error.message, 'canceled')) {
        logError(error.message, 'Error');
      }
    }
  };

  /**
   * Selects the bookmark whose preview strip was clicked. Shift, control and meta clicks are swallowed so the drag-select layer keeps them; any other click reports the bookmark and its grid index to the parent.
   *
   * @param e - The mouse event from the preview strip.
   * @returns Nothing.
   */
  const onClick = (e: any) => {
    if (e?.shiftKey || e?.ctrlKey || e?.metaKey) {
      e?.stopPropagation();
      e?.preventDefault();
      return;
    }
    onClickSelectEvent(bookmark, index);
  };

  useEffect(() => {
    isActiveQuery = true;
    if (isRenderViewerEcg) {
      downloadEventStrip();
    }
    return () => {
      isActiveQuery = false;
    };
  }, [bookmark, beatChanges, isRenderViewerEcg]);

  useEffect(
    () => () => {
      storeTimeout.removeStore(activeButton as any, timeoutRef.current);
    },
    [activeButton],
  );

  const channels = channelsProp?.includes(4) ? [bookmark.channel || 2] : channelsProp;

  return {
    isDeleted,
    ecgData,
    beatMarkers,
    hesBeatStatus,
    channels,
    onClick,
  };
};

export default useBookmarkPreviewStrip;
